import React from "react";
import "./style.css";

const ContactsTwoPrivacyPolicy = () => {
  return (
    <div className="contactsTwoPrivacyPolicyBody">
      <main className="contactsTwoPrivacyPolicyMain">
        <h1>Privacy Policy for Contacts</h1>
        <p>Last Updated: October 17, 2026</p>
        <p>
          Welcome to Contacts (the "Application"), a phone dialer, contacts
          manager and caller ID application. This Privacy Policy explains how
          we ("we," "our," or "us") collect, use, disclose, and safeguard your
          information when you use our Application. Our primary goal is to
          provide a secure, on-device calling and contacts experience that
          respects your privacy.
        </p>

        <h2>1. Contacts, Calls &amp; Local Processing</h2>
        <p>
          Our Application lets you manage your contacts, make and receive
          calls (optionally as your device's default phone app), view call
          history, block numbers, and use tools like Speed Dial, Emergency
          Contacts, Quick Response, Call Wallpaper/Ringtone, Call Reminders,
          and Import/Export of your contacts. How we handle this data:
        </p>
        <ul>
          <li>
            <strong>100% Local Processing:</strong> Your contacts, call logs,
            and messages sent from the app are read, stored, and processed
            entirely on your device, using Android's own Contacts and Call Log
            providers.
          </li>
          <li>
            <strong>No Cloud Uploads:</strong> We do NOT collect, upload, or
            transmit the content of your contacts, call history, or messages
            to our servers or any third-party servers.
          </li>
          <li>
            <strong>Recycle Bin:</strong> Deleted contacts are moved to an
            on-device Recycle Bin so you can restore them, until you
            permanently delete them or uninstall the Application.
          </li>
          <li>
            <strong>Import/Export:</strong> The vCard (.vcf) import and export
            tool reads or writes a file you pick yourself, directly on your
            device. We never see or receive that file.
          </li>
        </ul>

        <h2>2. Device Permissions Required</h2>
        <p>
          To function as a full-featured phone dialer app (including as your
          device's default phone app), our Application requires the following
          permissions. Each is used strictly for the feature described, never
          for unauthorized data collection:
        </p>
        <ul>
          <li>
            <strong>Contacts (Read &amp; Write):</strong> Used to display,
            create, edit, and delete your contacts, and to match incoming/
            outgoing phone numbers to a contact's name and photo.
          </li>
          <li>
            <strong>Call Phone, Phone State &amp; Answer Calls:</strong> Core
            permissions required to place calls, detect call state, and
            (when set as default phone app) answer/decline calls directly
            from our own call screen.
          </li>
          <li>
            <strong>Call Log (Read):</strong> Used to show your call history
            in the Recents tab.
          </li>
          <li>
            <strong>Call Log (Write):</strong> Used only to delete a call log
            entry when you remove it from your Recents list.
          </li>
          <li>
            <strong>Manage Own Calls:</strong> Declared to meet Android's
            default-dialer eligibility requirements on some devices. It is
            not used to place or manage any call directly — all calls are
            handled through the standard Android Telecom in-call service.
          </li>
          <li>
            <strong>Foreground Service &amp; Foreground Service (Phone
            Call):</strong> Keeps our call-handling service running (so the
            operating system does not stop it) for as long as a call is
            active.
          </li>
          <li>
            <strong>SMS (Send):</strong> Used only by the optional Quick
            Response feature — on the live call screen and the After Call
            screen — to send a preset text reply when you decline or have
            just finished a call, if you choose to use it.
          </li>
          <li>
            <strong>Audio Files:</strong> Used only so you can browse your
            device's audio files when choosing a custom ringtone for a
            contact.
          </li>
          <li>
            <strong>Write Settings:</strong> Used only if you choose to set a
            custom system ringtone from within the app.
          </li>
          <li>
            <strong>Display Over Other Apps:</strong> Used for the optional
            After Call screen (a brief overlay shown right after a call ends
            on some devices) and during onboarding on certain device brands
            (e.g. MIUI). You can disable the After Call screen at any time in
            Settings.
          </li>
          <li>
            <strong>Full-Screen Notifications:</strong> Used so an incoming
            call, and the After Call screen, can show their full-screen UI
            even when your device is locked.
          </li>
          <li>
            <strong>Notifications:</strong> Used to alert you about incoming/
            ongoing calls, the After Call screen, and Call Reminders you set.
          </li>
          <li>
            <strong>Schedule Exact Alarm:</strong> Used to trigger Call
            Reminders at the precise time you choose.
          </li>
          <li>
            <strong>Vibrate:</strong> Used for a short haptic tap when you
            dial a digit on the Keypad.
          </li>
        </ul>

        <h2>3. Information Collected Automatically</h2>
        <p>
          While we never collect your contacts, call log content, or
          messages, we do collect limited technical and usage information to
          maintain and improve the Application:
        </p>
        <ul>
          <li>
            <strong>Device Information:</strong> Device model, operating
            system version, screen resolution, language, and application
            version.
          </li>
          <li>
            <strong>Usage Data / Analytics:</strong> Anonymous interaction
            data — such as which screens you view, when a call is placed, a
            contact is created/updated/deleted, a number is blocked, your
            call history is cleared, a ringtone is set, an app-update prompt
            is shown/accepted/dismissed, and whether you open the in-app
            review flow — collected via Firebase Analytics, along with crash
            reports via Firebase Crashlytics, to identify bugs and improve
            app stability. We also record, as anonymous user properties,
            whether the Application is currently set as your default phone
            app, and your chosen app theme and language. None of this data
            includes the actual phone numbers, names, or content involved.
          </li>
          <li>
            <strong>Advertising Identifiers:</strong> Identifiers such as the
            Google Advertising ID (GAID) are used to support ad
            personalization and measure ad performance.
          </li>
        </ul>

        <h2>4. Third-Party Services</h2>
        <p>
          We use the following trusted third-party services. None of them can
          access the content of your contacts, calls, or messages:
        </p>
        <ul>
          <li>
            <strong>Google Analytics &amp; Crashlytics (Firebase):</strong>{" "}
            Collects anonymous usage data and crash reports. Learn more at the{" "}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noreferrer"
            >
              Google Privacy Policy
            </a>
            .
          </li>
          <li>
            <strong>Google AdMob:</strong> Displays advertisements within the
            Application and may collect your Advertising ID. Learn more at{" "}
            <a
              href="https://policies.google.com/technologies/ads"
              target="_blank"
              rel="noreferrer"
            >
              Google's Privacy &amp; Terms
            </a>
            .
          </li>
          <li>
            <strong>Google Play In-App Updates &amp; Reviews:</strong> Used to
            check for and install app updates and to request an in-app rating
            directly through Google Play.
          </li>
        </ul>

        <h2>5. How We Use the Information</h2>
        <p>
          The automatically collected technical data is used exclusively to:
        </p>
        <ul>
          <li>Operate, maintain, and improve the Application's stability.</li>
          <li>
            Serve relevant advertisements that support the free use of the
            app.
          </li>
          <li>Understand feature usage to guide future improvements.</li>
        </ul>

        <h2>6. Data Retention</h2>
        <p>
          We only retain data for as long as necessary to provide the
          Application's features:
        </p>
        <ul>
          <li>
            <strong>Local Data:</strong> Your contacts, call logs, and app
            settings remain on your device until you manually delete them or
            uninstall the Application. Contacts you delete are kept in the
            in-app Recycle Bin until you restore them, permanently delete
            them, or uninstall the app.
          </li>
          <li>
            <strong>Analytics &amp; Usage Data:</strong> Anonymous data
            collected via third-party services (like Firebase) is retained
            according to their respective privacy policies, typically for a
            period of up to 14 months, before being automatically deleted.
          </li>
        </ul>

        <h2>7. Data Security</h2>
        <p>
          We implement commercially reasonable security measures to protect
          your data. Since your contacts and call logs are processed entirely
          on your device and never transmitted to our servers, the risk of
          that data being intercepted through our systems is non-existent.
        </p>

        <h2>8. Your Rights &amp; Choices (GDPR/CCPA)</h2>
        <p>
          Depending on your location, you may have specific rights regarding
          your personal data (such as the right to access, delete, or
          restrict processing). Because we process your contacts and calls
          entirely locally, you have full control over your data directly
          within the app:
        </p>
        <ul>
          <li>
            <strong>Delete Your Data:</strong> Since all personal data lives
            on your device, you can exercise your right to erasure simply by
            deleting contacts within the app, clearing your Recycle Bin, or
            uninstalling the Application entirely.
          </li>
          <li>
            <strong>Opt-out of Personalized Ads:</strong> You can opt out via
            your Android Device Settings (Settings &gt; Google &gt; Ads &gt;
            Opt out of Ads Personalization) or by visiting{" "}
            <a
              href="https://adssettings.google.com/"
              target="_blank"
              rel="noreferrer"
            >
              Google Ad Settings
            </a>
            .
          </li>
          <li>
            <strong>Revoke Permissions:</strong> You can revoke Contacts,
            Call Log, SMS, or any other permission at any time through your
            device settings, though this will limit the corresponding
            feature.
          </li>
          <li>
            <strong>Change Default Phone App:</strong> You can change your
            device's default phone app at any time from your device's
            Settings &gt; Apps &gt; Default apps.
          </li>
        </ul>

        <h2>9. Children's Privacy</h2>
        <p>
          Our Application is not directed at children under the age of 13. We
          do not knowingly collect personal information from children. If we
          discover that data has been collected from a child under 13
          inadvertently through a third-party service, we will take steps to
          ensure it is deleted.
        </p>

        <h2>10. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy periodically to reflect changes
          in our practices or regulatory requirements. We encourage you to
          review this page occasionally. Your continued use of the
          Application after changes are posted constitutes your acceptance of
          the new policy.
        </p>

        <h2>11. Contact Us</h2>
        <p>
          If you have any questions, concerns, or feedback regarding this
          Privacy Policy or our data practices, please contact us at:
        </p>
        <ul>
          <li>
            Developed By: <span>Aavakar Solution</span>
          </li>
          <li>
            By email:{" "}
            <a
              href="mailto:parth@aavakar.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              parth@aavakar.com
            </a>
          </li>
        </ul>
      </main>
    </div>
  );
};

export default ContactsTwoPrivacyPolicy;
